// 🔧 content_bundle_key_helpers.js
// ✅ Purpose: Canonical helper utilities for creating and parsing Content Engine bundle keys
// ✅ Features:
// - Enforces approved bundle key format: scopeId_locale_profile_area
// - Supports low-cost Firestore reads by loading one published content bundle per area
// - Prepares for future organization white-label bundles
// - Prepares for future locale/profile/market-specific bundles
// - Keeps bundle key creation centralized so pages, resolvers, and bundle builders stay aligned

import {
  DEFAULT_SCOPE_ID,
} from "../constants/content_scopes";

import {
  DEFAULT_CONTENT_LOCALE,
} from "../constants/content_locales";

import {
  DEFAULT_CONTENT_PROFILE,
} from "../constants/content_profiles";

import {
  normalizeContentKeySegment,
} from "./content_key_helpers";

/**
 * Separator used for Content Engine bundle keys.
 *
 * Approved format:
 * scopeId_locale_profile_area
 *
 * Examples:
 * global_en-US_default_event_home
 * org_abc123_en-US_default_event_home
 * org_abc123_es-MX_automotive_event_home
 */
export const CONTENT_BUNDLE_KEY_SEPARATOR = "_";

/**
 * Normalizes bundle scope IDs.
 *
 * Organization IDs may eventually include prefixes such as:
 * org_abc123
 *
 * Phase 1 uses:
 * global
 */
export function normalizeBundleScopeId(scopeId = DEFAULT_SCOPE_ID) {
  return normalizeContentKeySegment(scopeId || DEFAULT_SCOPE_ID);
}

/**
 * Normalizes locale values for bundle keys.
 *
 * Important:
 * We preserve the locale hyphen because locale values are standardized
 * as BCP-style values such as en-US and es-MX.
 *
 * We do not use normalizeContentKeySegment here because that would turn:
 * en-US -> en_us
 *
 * Bundle keys intentionally preserve:
 * en-US
 */
export function normalizeBundleLocale(locale = DEFAULT_CONTENT_LOCALE) {
  return String(locale || DEFAULT_CONTENT_LOCALE).trim();
}

/**
 * Normalizes profile values.
 */
export function normalizeBundleProfile(profile = DEFAULT_CONTENT_PROFILE) {
  return normalizeContentKeySegment(profile || DEFAULT_CONTENT_PROFILE);
}

/**
 * Normalizes area values.
 */
export function normalizeBundleArea(area = "") {
  return normalizeContentKeySegment(area);
}

/**
 * Builds a canonical content bundle key.
 *
 * Required final shape:
 * scopeId_locale_profile_area
 *
 * Phase 1 example:
 * global_en-US_default_event_home
 */
export function buildContentBundleKey({
  scopeId = DEFAULT_SCOPE_ID,
  locale = DEFAULT_CONTENT_LOCALE,
  profile = DEFAULT_CONTENT_PROFILE,
  area,
}) {
  const normalizedScopeId = normalizeBundleScopeId(scopeId);
  const normalizedLocale = normalizeBundleLocale(locale);
  const normalizedProfile = normalizeBundleProfile(profile);
  const normalizedArea = normalizeBundleArea(area);

  if (!normalizedScopeId || !normalizedLocale || !normalizedProfile || !normalizedArea) {
    console.error("[Content Engine] Missing bundle key segment.", {
      scopeId,
      locale,
      profile,
      area,
      normalizedScopeId,
      normalizedLocale,
      normalizedProfile,
      normalizedArea,
    });

    throw new Error(
      "Content Engine bundle keys require scopeId, locale, profile, and area."
    );
  }

  return [
    normalizedScopeId,
    normalizedLocale,
    normalizedProfile,
    normalizedArea,
  ].join(CONTENT_BUNDLE_KEY_SEPARATOR);
}

/**
 * Parses a bundle key into its expected parts.
 *
 * NOTE:
 * This parser assumes:
 * - scopeId does not contain underscores beyond known normalized form
 * - profile does not contain underscores
 * - area may contain underscores
 *
 * Because LGV areas commonly include underscores, we parse the first
 * three segments as scopeId, locale, and profile, then rejoin the rest
 * as the area.
 *
 * Example:
 * global_en-US_default_event_home
 *
 * Returns:
 * {
 *   scopeId: "global",
 *   locale: "en-US",
 *   profile: "default",
 *   area: "event_home",
 *   isValid: true
 * }
 */
export function parseContentBundleKey(bundleKey = "") {
  const parts = String(bundleKey).split(CONTENT_BUNDLE_KEY_SEPARATOR);

  if (parts.length < 4) {
    console.error("[Content Engine] Invalid bundle key format.", {
      bundleKey,
      expectedFormat: "scopeId_locale_profile_area",
    });

    return {
      scopeId: "",
      locale: "",
      profile: "",
      area: "",
      isValid: false,
    };
  }

  const [scopeId, locale, profile, ...areaParts] = parts;

  const area = areaParts.join(CONTENT_BUNDLE_KEY_SEPARATOR);

  return {
    scopeId,
    locale,
    profile,
    area,
    isValid: Boolean(scopeId && locale && profile && area),
  };
}

/**
 * Validates a bundle key.
 */
export function isValidContentBundleKey(bundleKey = "") {
  return parseContentBundleKey(bundleKey).isValid === true;
}

/**
 * Safely builds a content bundle key but returns a fallback instead of throwing.
 */
export function tryBuildContentBundleKey({
  scopeId = DEFAULT_SCOPE_ID,
  locale = DEFAULT_CONTENT_LOCALE,
  profile = DEFAULT_CONTENT_PROFILE,
  area,
  fallback = "",
}) {
  try {
    return buildContentBundleKey({
      scopeId,
      locale,
      profile,
      area,
    });
  } catch (error) {
    console.error("[Content Engine] Failed to build content bundle key.", {
      scopeId,
      locale,
      profile,
      area,
      error,
    });

    return fallback;
  }
}

/**
 * Builds the memory cache key for an area bundle.
 *
 * For Phase 1, cache keys and Firestore bundle keys intentionally match.
 * This keeps diagnostics simple.
 */
export function buildContentCacheKey({
  scopeId = DEFAULT_SCOPE_ID,
  locale = DEFAULT_CONTENT_LOCALE,
  profile = DEFAULT_CONTENT_PROFILE,
  area,
}) {
  return buildContentBundleKey({
    scopeId,
    locale,
    profile,
    area,
  });
}